/* Photography-derived suggestions. These are never presented as paint brand matches. */
(() => {
  if (customElements.get('zenvana-paint-palette')) return;
  const hex = rgb => '#' + rgb.map(v => Math.round(v).toString(16).padStart(2, '0')).join('').toUpperCase();
  // OKLab separates hue from lightness, so muted pink and olive are not
  // averaged into another beige. Input and output stay sampled image colours.
  const lab = rgb => {
    const [r, g, b] = rgb.map(v => { v /= 255; return v <= .04045 ? v / 12.92 : ((v + .055) / 1.055) ** 2.4; });
    const l = Math.cbrt(.4122214708*r + .5363325363*g + .0514459929*b);
    const m = Math.cbrt(.2119034982*r + .6806995451*g + .1073969566*b);
    const s = Math.cbrt(.0883024619*r + .2817188376*g + .6299787005*b);
    return [.2104542553*l + .793617785*m - .0040720468*s, 1.9779984951*l - 2.428592205*m + .4505937099*s, .0259040371*l + .7827717662*m - .808675766*s];
  };
  const distance = (a, b) => .12*(a[0]-b[0])**2 + 4*(a[1]-b[1])**2 + 4*(a[2]-b[2])**2;
  let sourceCatalog;
  const loadSources = url => sourceCatalog ||= fetch(url).then(response => { if (!response.ok) throw new Error('Design sources unavailable'); return response.json(); }).catch(error => { sourceCatalog = null; throw error; });
  const finishes = { solid: 'Solid', vertical: 'Fine vertical', wash: 'Soft wash', horizontal: 'Horizontal bands' };
  class PaintPalette extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.dialog = this.querySelector('dialog');
      this.launch = this.querySelector('[data-paint-open]');
      if (!this.dialog?.showModal) return;
      this.launch.hidden = false;
      this.dialog.dataset.finish = 'solid';
      this.querySelectorAll('[data-paint-finish]').forEach(button => {
        button.addEventListener('click', () => this.chooseFinish(button.dataset.paintFinish), options);
      });
      this.querySelector('[data-paint-enlarge]').addEventListener('click', () => this.expand(true), options);
      this.querySelector('[data-paint-shrink]').addEventListener('click', () => this.expand(false), options);
      this.dialog.addEventListener('cancel', event => {
        if (!this.querySelector('[data-paint-expanded]').hidden) { event.preventDefault(); this.expand(false); }
      }, options);
      this.launch.addEventListener('click', () => this.open(), options);
      this.querySelector('[data-paint-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        this.expand(false, false);
        document.body.style.overflow = this.previousOverflow ?? '';
        this.launch.focus({ preventScroll: true });
      }, options);
      this.querySelector('[data-paint-copy]').addEventListener('click', async () => {
        const status = this.querySelector('[data-paint-copy-status]');
        try { await navigator.clipboard.writeText(this.selected); status.textContent = 'Colour code copied.'; }
        catch { status.textContent = 'Copy this reference: ' + this.selected; }
      }, options);
    }
    disconnectedCallback() {
      this.events?.abort();
      if (this.dialog?.open) { this.dialog.close(); document.body.style.overflow = this.previousOverflow ?? ''; }
    }
    async pickPhotograph() {
      // Brightness cannot distinguish pale wallpaper from windows or ceilings.
      // Keep the merchant's cover until the hourly visual review records a
      // wall-dominant photograph and a design-only sampling area in sources.
      return this.dataset.image;
    }
    async open() {
      this.previousOverflow = document.body.style.overflow;
      this.dialog.showModal();
      this.dialog.scrollTop = 0;
      this.querySelector('.z-paint__body').scrollTop = 0;
      document.body.style.overflow = 'hidden';
      if (this.prepared || this.loading) return;
      this.loading = true;
      try {
        const source = this.dataset.artwork === 'true' ? null : (await loadSources(this.dataset.sources).catch(() => ({})))[this.dataset.handle];
        const artwork = this.dataset.artwork === 'true' || source?.artwork;
        this.querySelector('[data-paint-source-note]').textContent = artwork ? 'Colours alongside your design artwork.' : 'Your full product photograph, with coordinating colours alongside.';
        this.querySelector('[data-paint-colour-note]').textContent = artwork ? 'Tones drawn from this design.' : 'Tones sampled from the wallpaper area.';
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.src = this.dataset.artwork === 'true' ? this.dataset.image : source ? source.image + '&width=1600' : await this.pickPhotograph();
        await image.decode();
        // Sampling and display framing are separate. Only a manually reviewed
        // display_crop may hide architecture; sampling never changes the view.
        let crop = source?.crop || (this.dataset.crop || '0.18,0.12,0.64,0.36').split(',').map(Number);
        if (crop.length !== 4 || crop.some(n => !Number.isFinite(n)) || crop[2] <= 0 || crop[3] <= 0) crop = [.18, .12, .64, .36];
        const [x, y, w, h] = crop;
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, 1600 / (image.width * w), 1600 / (image.height * h));
        canvas.width = Math.max(1, Math.round(image.width * w * scale));
        canvas.height = Math.max(1, Math.round(image.height * h * scale));
        canvas.getContext('2d').drawImage(image, image.width * x, image.height * y, image.width * w, image.height * h, 0, 0, canvas.width, canvas.height);
        const design = this.querySelector('.z-paint__design img');
        design.loading = 'eager';
        design.removeAttribute('srcset');
        design.removeAttribute('sizes');
        design.src = image.src;
        design.width = image.width; design.height = image.height;
        await design.decode();
        const frame = source?.display_crop;
        const framed = Array.isArray(frame) && frame.length === 4 && frame.every(Number.isFinite)
          && frame[0] >= 0 && frame[1] >= 0 && frame[2] > 0 && frame[3] > 0
          && frame[0] + frame[2] <= 1.000001 && frame[1] + frame[3] <= 1.000001;
        const viewport = this.querySelector('.z-paint__design');
        viewport.dataset.framed = String(framed);
        let ratio = image.width / image.height;
        if (framed) {
          const [left, top, width, height] = frame;
          ratio *= width / height;
          viewport.style.setProperty('--frame-width', 100 / width + '%');
          viewport.style.setProperty('--frame-left', -100 * left / width + '%');
          viewport.style.setProperty('--frame-top', -100 * top / height + '%');
          this.querySelector('[data-paint-source-note]').textContent = 'A framed view of your design, with coordinating colours alongside.';
        }
        this.dialog.style.setProperty('--paint-image-ratio', ratio);
        this.querySelector('.z-paint__scene').hidden = false;
        const sample = document.createElement('canvas');
        sample.width = sample.height = 100;
        const ctx = sample.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(canvas, 0, 0, 100, 100);
        const pixels = ctx.getImageData(0, 0, 100, 100).data;
        const bins = new Map();
        for (let i = 0; i < pixels.length; i += 4) {
          if (pixels[i + 3] < 200) continue;
          const rgb = [pixels[i], pixels[i + 1], pixels[i + 2]];
          const key = rgb.map(v => Math.floor(v / 12)).join(',');
          const bin = bins.get(key) || { sum: [0, 0, 0], count: 0 };
          bin.count++; rgb.forEach((v, n) => bin.sum[n] += v); bins.set(key, bin);
        }
        // Keep substantial accents as well as the dominant background. Select
        // by perceptual separation and coverage, rather than six light/dark greys.
        const clusters = [];
        for (const bin of [...bins.values()].sort((a, b) => b.count - a.count)) {
          const rgb = bin.sum.map(v => v / bin.count);
          const tone = lab(rgb);
          const near = clusters.find(c => distance(c.lab, tone) < .0003);
          if (near) {
            near.rgb = near.rgb.map((v, i) => (v * near.count + rgb[i] * bin.count) / (near.count + bin.count));
            near.count += bin.count;
            near.lab = lab(near.rgb);
          } else clusters.push({ rgb, lab: tone, count: bin.count });
        }
        const candidates = clusters.filter(c => c.count >= 30).sort((a, b) => b.count - a.count);
        const chosen = candidates.length ? [candidates.shift()] : [];
        while (chosen.length && chosen.length < 6 && candidates.length) {
          const ranked = candidates.map(c => ({ c, separation: Math.min(...chosen.map(t => distance(t.lab, c.lab))) }))
            .filter(t => t.separation > .0007)
            .sort((a, b) => b.separation * Math.sqrt(b.c.count) - a.separation * Math.sqrt(a.c.count));
          if (!ranked.length) break;
          const next = ranked[0].c;
          chosen.push(next);
          candidates.splice(candidates.indexOf(next), 1);
        }
        const palette = chosen.map(c => c.rgb.map(Math.round));
        if (!palette.length) throw new Error('No colours available');
        const target = this.querySelector('[data-paint-swatches]');
        target.replaceChildren(...palette.map(rgb => this.swatch(rgb, () => this.chooseBase(rgb))));
        this.querySelector('[data-paint-status]').hidden = true;
        this.querySelector('[data-paint-ready]').hidden = false;
        this.chooseBase(palette[0]);
        this.prepared = true;
        this.querySelector('.z-paint__body').scrollTop = 0;
      } catch {
        this.querySelector('[data-paint-status]').textContent = 'The palette could not load. Please close and reopen to try again, or ask us for colour advice.';
      } finally { this.loading = false; }
    }
    swatch(rgb, action) {
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'z-paint__swatch';
      const code = hex(rgb);
      button.style.setProperty('--tone', code);
      button.dataset.code = code;
      button.setAttribute('aria-label', 'Preview colour ' + code);
      button.setAttribute('aria-pressed', 'false');
      const label = document.createElement('span'); label.textContent = code;
      button.append(label); button.addEventListener('click', action);
      return button;
    }
    chooseBase(rgb) {
      const tones = [.2, .4, .6].map(amount => rgb.map(v => v + (255 - v) * amount));
      this.querySelector('[data-paint-tones]').replaceChildren(...tones.map(tone => this.swatch(tone, () => this.select(tone))));
      this.select(rgb);
    }
    chooseFinish(finish) {
      if (!finishes[finish]) return;
      this.dialog.dataset.finish = finish;
      this.querySelectorAll('[data-paint-finish]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.paintFinish === finish)));
      this.querySelector('[data-paint-finish-name]').textContent = finishes[finish];
      this.querySelector('[data-paint-expanded-name]').textContent = finishes[finish];
      this.querySelector('[data-paint-enlarge]').setAttribute('aria-label', 'Enlarge ' + finishes[finish].toLowerCase() + ' preview');
    }
    expand(open, focus = true) {
      this.querySelector('[data-paint-expanded]').hidden = !open;
      this.querySelector('.z-paint__header').inert = open;
      this.querySelector('.z-paint__body').inert = open;
      this.dialog.classList.toggle('z-paint__dialog--expanded', open);
      const body = this.querySelector('.z-paint__body');
      if (open) { this.savedScroll = body.scrollTop; body.scrollTop = 0; }
      else if (this.savedScroll != null) body.scrollTop = this.savedScroll;
      if (focus) this.querySelector(open ? '[data-paint-shrink]' : '[data-paint-enlarge]').focus({ preventScroll: true });
    }
    select(rgb) {
      this.selected = hex(rgb);
      this.dialog.style.setProperty('--paint', this.selected);
      this.dialog.style.setProperty('--paint-light', hex(rgb.map(v => v + (255 - v) * .16)));
      this.dialog.style.setProperty('--paint-deep', hex(rgb.map(v => v * .90)));
      this.querySelector('[data-paint-expanded-code]').textContent = this.selected;
      this.querySelector('[data-paint-chip]').style.background = this.selected;
      this.querySelector('[data-paint-code]').textContent = this.selected;
      this.querySelector('[data-paint-copy-status]').textContent = '';
      this.querySelectorAll('[data-code]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.code === this.selected)));
    }
  }
  customElements.define('zenvana-paint-palette', PaintPalette);
})();
